// ---------------------------------------------------------------------------
// Reddit Pixel loader + guarded event wrapper.
//
// Reddit's setup instructions say to paste their snippet directly into <head>.
// We deliberately DON'T. A hardcoded snippet loads for every visitor on first
// paint, before anyone has agreed to anything — exactly what this app's consent
// system exists to prevent, and what GDPR requires consent BEFORE for EU/UK
// visitors. The pixel drops the `_rdt_uuid` cookie and is unambiguously
// marketing.
//
// So it is reimplemented here behind the same gate as the Meta pixel and the
// LinkedIn tag: nothing loads until `isMarketingGranted()`, every event routes
// through `trackReddit()` which no-ops until then, and withdrawing consent
// unloads the tag (`unloadRedditPixel`). Like the Insight Tag — and unlike Meta
// — the Reddit pixel reports its own page visit once resident, so the teardown
// direction of the gate genuinely matters.
//
// Network load is gated to production builds, matching `metaPixel.ts` and
// `linkedinTag.ts`: dev and preview traffic never reaches the real ad account.
//
// The pixel only ever sees marketing signals — that a page was viewed or a
// lead / signup happened. Never idea, memo, or session content. Do not pass
// idea text, titles, or memo bodies into any event params here.
//
// The `rdt_cid` click id (Reddit's equivalent of `fbclid`) is captured for the
// SERVER-side Conversions API, not here — it lives in `metaPixel.ts`'s shared
// `capiSignals()` bag alongside `li_fat_id`, so all server match signals are
// read in one place. See `captureRdtCidFromUrl()` there.
// ---------------------------------------------------------------------------

import { isMarketingGranted, subscribeConsent } from './consentManager';

/**
 * The Reddit Pixel this site reports to. Must match `RDT_PIXEL_ID` in the
 * backend `common/redditCapi.ts`, or the browser events and the server
 * Conversions API events land on different pixels and never deduplicate.
 */
const RDT_PIXEL_ID = 'a2_jbu4y0qhv81n';
const IS_PROD = import.meta.env.PROD;

/** Reddit's standard browser event names (title-case, unlike CAPI v3's UPPER_SNAKE). */
type RedditEvent = 'PageVisit' | 'ViewContent' | 'Lead' | 'SignUp' | 'Purchase';

interface RedditEventParams {
  /**
   * Shared with the same conversion sent server-side, so Reddit collapses the
   * browser and server copies into one instead of counting two. Reddit's
   * recommended (and, when running pixel + CAPI together, required) dedup key.
   */
  conversionId?: string;
  value?: number;
  currency?: string;
}

declare global {
  interface Window {
    rdt?: ((...args: unknown[]) => void) & { callQueue?: unknown[]; sendEvent?: unknown };
  }
}

let scriptInjected = false;
let initialized = false;
/** The injected <script>, held so revocation can remove it. */
let injectedScript: HTMLScriptElement | null = null;

/**
 * Inject Reddit's pixel.js and wire the `rdt` queue stub. Idempotent — the
 * first call loads the library; later calls are no-ops. Only ever called once
 * consent is granted.
 *
 * This is Reddit's own bootstrap, with the same hardening applied to the Meta
 * and LinkedIn snippets: their version inserts before the first <script> in the
 * document and assumes one exists, so this falls back to <head> when it does
 * not. Without that, `s.parentNode` is null and the tag silently never loads.
 */
function loadRedditScript(): void {
  if (scriptInjected || typeof window === 'undefined') return;
  scriptInjected = true;

  if (!window.rdt) {
    const stub = ((...args: unknown[]) => {
      const p = window.rdt!;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (p as any).sendEvent ? (p as any).sendEvent(...args) : p.callQueue!.push(args);
    }) as NonNullable<Window['rdt']>;
    stub.callQueue = [];
    window.rdt = stub;
  }

  const script = document.createElement('script');
  script.async = true;
  script.src = 'https://www.redditstatic.com/ads/pixel.js';
  injectedScript = script;

  const first = document.getElementsByTagName('script')[0];
  if (first?.parentNode) first.parentNode.insertBefore(script, first);
  else document.head.appendChild(script);
}

/**
 * Ensure the pixel is loaded + initialized. Safe to call repeatedly. In dev it
 * marks itself initialized without a network load so events log cleanly.
 */
function ensureInitialized(): void {
  if (initialized) return;

  if (!IS_PROD) {
    initialized = true;
    console.info('[reddit-pixel] (dev) init', RDT_PIXEL_ID);
    return;
  }

  loadRedditScript();
  window.rdt?.('init', RDT_PIXEL_ID);
  initialized = true;
}

/**
 * The one guarded entry point for every Reddit event. No-ops entirely unless
 * marketing consent is granted, then lazily loads + inits the pixel on the
 * first allowed event and forwards the call.
 */
export function trackReddit(event: RedditEvent, params?: RedditEventParams): void {
  if (!isMarketingGranted()) return;
  ensureInitialized();

  // Reddit expects a flat payload. `conversion_id` is the dedup key; value and
  // currency ride along on monetary events (currency only sent with a value —
  // a currency with no value carries no information).
  const payload: Record<string, string | number> = {};
  if (params?.conversionId) payload.conversion_id = params.conversionId;
  if (typeof params?.value === 'number' && params.value > 0) {
    payload.value = params.value;
    payload.currency = (params.currency ?? 'USD').toUpperCase();
  }

  if (!IS_PROD) {
    console.info('[reddit-pixel] (dev) track', event, payload);
    return;
  }
  window.rdt?.('track', event, payload);
}

/** Convenience wrapper for the most common event. */
export function trackRedditPageVisit(): void {
  trackReddit('PageVisit');
}

/**
 * Install the pixel lifecycle. Called once at app start:
 *   - If consent is already granted (stored choice, or opt-out region on first
 *     load), initialize immediately and fire the first PageVisit.
 *   - Otherwise wait; the moment consent flips to granted, initialize and fire
 *     the PageVisit the visitor's landing view should have counted.
 *
 * Like the LinkedIn tag, the Reddit pixel reports page visits itself once
 * loaded, so this runs BOTH ways: an opt-out unloads an already-resident tag
 * rather than leaving it reporting against a withdrawn choice.
 *
 * Returns an unsubscribe function (used only in dev/StrictMode cleanup).
 */
export function installRedditPixelLifecycle(): () => void {
  let firedInitialPageVisit = false;

  const onConsent = () => {
    if (isMarketingGranted()) {
      if (!firedInitialPageVisit) {
        firedInitialPageVisit = true;
        ensureInitialized();
        trackRedditPageVisit();
      }
      return;
    }
    if (initialized) {
      unloadRedditPixel();
      firedInitialPageVisit = false;
    }
  };

  onConsent();
  return subscribeConsent(onConsent);
}

/**
 * Unload the pixel on consent withdrawal. Best-effort: code already executed
 * cannot be un-run, but no further event is sent, the cookie the pixel sets is
 * expired, and the latches reset so a re-grant loads cleanly.
 */
function unloadRedditPixel(): void {
  if (typeof window === 'undefined') return;

  injectedScript?.remove();
  injectedScript = null;
  delete window.rdt;

  // `_rdt_uuid` is the pixel's own first-party cookie.
  expireCookie('_rdt_uuid');

  scriptInjected = false;
  initialized = false;
}

/** Expire a cookie across the plausible path/domain combinations. */
function expireCookie(name: string): void {
  const past = 'Thu, 01 Jan 1970 00:00:00 GMT';
  const host = window.location.hostname;
  const parts = host.split('.');
  const registrable = parts.length > 2 ? parts.slice(-2).join('.') : host;
  for (const domain of [undefined, host, `.${host}`, `.${registrable}`]) {
    document.cookie =
      `${name}=; expires=${past}; path=/` + (domain ? `; domain=${domain}` : '');
  }
}
